import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded"
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded"
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded"
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createDebt,
  deleteDebt,
  getDebtById,
  getDebtFormData,
  getDebts,
  updateDebt,
} from "../../services/debtService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultFormData = {
  company_id: 1,
  debt_type: "",
  customer_id: "",
  store_id: "",
  user_id: "",
  reference_type: "",
  reference_id: "",
  external_reference: "",
  original_amount: "",
  paid_amount: 0,
  status: "open",
  due_date: "",
  description: "",
  notes: "",
  created_by: 1,
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number =
    Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatAmount(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function formatDate(value) {
  if (!value) {
    return "-"
  }

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-"
  }

  return date.toLocaleDateString(
    "fr-FR"
  )
}


function formatDateTimeForInput(value) {
  if (!value) {
    return ""
  }

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return ""
  }

  const offset =
    date.getTimezoneOffset()

  const localDate =
    new Date(
      date.getTime() -
        offset * 60000
    )

  return localDate
    .toISOString()
    .slice(
      0,
      16
    )
}


function getStatusFallbackLabel(value) {
  const labels = {
    open: "Ouverte",
    partial: "Partiellement payée",
    paid: "Payée",
    cancelled: "Annulée",
  }

  return (
    labels[value] ||
    value ||
    "-"
  )
}


function getStatusColor(value) {
  if (
    value === "paid"
  ) {
    return "success"
  }

  if (
    value === "partial"
  ) {
    return "warning"
  }

  if (
    value === "open"
  ) {
    return "info"
  }

  if (
    value === "cancelled"
  ) {
    return "error"
  }

  return "default"
}


function getDebtRowClass(status) {
  if (
    status === "paid"
  ) {
    return "debt-row debt-row--paid"
  }

  if (
    status === "partial"
  ) {
    return "debt-row debt-row--partial"
  }

  if (
    status === "open"
  ) {
    return "debt-row debt-row--open"
  }

  if (
    status === "cancelled"
  ) {
    return "debt-row debt-row--cancelled"
  }

  return "debt-row debt-row--default"
}


function getErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.35,
          sm: 1.5,
        },

        py: 1.15,

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.1,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            display: "grid",
            placeItems: "center",

            borderRadius: 2.5,

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.7,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 19,
                  sm: 21,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",

                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                minWidth: 0,

                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE CARD
// =========================================================

function DebtMobileCard({
  row,
  statusLabel,
  debtTypeLabel,
  onEdit,
  onDelete,
}) {
  const remaining =
    toNumber(
      row.remaining_amount
    )

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        boxShadow:
          row.status ===
          "paid"
            ? "inset 4px 0 0 #22c55e, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : row.status ===
              "partial"
            ? "inset 4px 0 0 #f59e0b, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : row.status ===
              "cancelled"
            ? "inset 4px 0 0 #ef4444, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : "inset 4px 0 0 #0ea5e9, 0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,

          "&:last-child": {
            pb: 1.5,
          },
        }}
      >
        <Stack spacing={1.2}>
          <Box
            sx={{
              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "flex-start",

              gap: 1,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                {row.debt_code ||
                  `DET-${row.id}`}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {debtTypeLabel(
                  row.debt_type
                )}
              </Typography>
            </Box>


            <Chip
              size="small"
              color={getStatusColor(
                row.status
              )}
              label={statusLabel(
                row.status
              )}
              sx={{
                maxWidth: 150,

                "& .MuiChip-label":
                  {
                    overflow:
                      "hidden",

                    textOverflow:
                      "ellipsis",
                  },
              }}
            />
          </Box>


          <Paper
            variant="outlined"
            sx={{
              p: 1,

              borderRadius: 2.5,

              bgcolor: "#fbfdff",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Concerné
            </Typography>

            <Typography
              variant="body2"
              fontWeight={800}
              color="#334155"
            >
              {row.customer_name ||
                row.store_name ||
                row.user_name ||
                "-"}
            </Typography>

            {row.store_name &&
            row.customer_name ? (
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Shop :{" "}
                {row.store_name}
              </Typography>
            ) : null}
          </Paper>


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                "repeat(3, minmax(0, 1fr))",

              gap: 0.7,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 0.85,

                borderRadius: 2,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Initial
              </Typography>

              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                {formatAmount(
                  row.original_amount
                )}
              </Typography>
            </Paper>


            <Paper
              variant="outlined"
              sx={{
                p: 0.85,

                borderRadius: 2,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Payé
              </Typography>

              <Typography
                fontWeight={900}
                color="success.main"
              >
                {formatAmount(
                  row.paid_amount
                )}
              </Typography>
            </Paper>


            <Paper
              variant="outlined"
              sx={{
                p: 0.85,

                borderRadius: 2,

                bgcolor:
                  remaining > 0
                    ? "rgba(220, 38, 38, 0.05)"
                    : "rgba(22, 163, 74, 0.05)",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Reste
              </Typography>

              <Typography
                fontWeight={900}
                color={
                  remaining > 0
                    ? "error.main"
                    : "success.main"
                }
              >
                {formatAmount(
                  row.remaining_amount
                )}
              </Typography>
            </Paper>
          </Box>


          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
            >
              Échéance
            </Typography>

            <Typography
              variant="body2"
              fontWeight={800}
            >
              {formatDate(
                row.due_date
              )}
            </Typography>
          </Box>


          {row.description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflowWrap:
                  "anywhere",
              }}
            >
              {row.description}
            </Typography>
          ) : null}


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                "repeat(2, minmax(0, 1fr))",

              gap: 0.7,
            }}
          >
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() =>
                onEdit(
                  row.id
                )
              }
            >
              Modifier
            </Button>


            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() =>
                onDelete(
                  row.id
                )
              }
            >
              Supprimer
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function DebtsPage() {
  const theme =
    useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    debts,
    setDebts,
  ] = useState([])

  const [
    customers,
    setCustomers,
  ] = useState([])

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    users,
    setUsers,
  ] = useState([])

  const [
    debtTypes,
    setDebtTypes,
  ] = useState([])

  const [
    statuses,
    setStatuses,
  ] = useState([])

  const [
    referenceTypes,
    setReferenceTypes,
  ] = useState([])


  // =======================================================
  // UI
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedDebt,
    setSelectedDebt,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("")

  const [
    debtTypeFilter,
    setDebtTypeFilter,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize:
      ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message:
        String(
          message || ""
        ),
    })
  }


  const handleCloseSnackbar =
    () => {
      setSnackbar(
        (previous) => ({
          ...previous,
          open: false,
        })
      )
    }


  // =======================================================
  // LABELS
  // =======================================================

  const getDebtTypeLabel =
    (value) => {
      return (
        debtTypes.find(
          (item) =>
            item.value ===
            value
        )?.label ||
        value ||
        "-"
      )
    }


  const getStatusLabel =
    (value) => {
      return (
        statuses.find(
          (item) =>
            item.value ===
            value
        )?.label ||
        getStatusFallbackLabel(
          value
        )
      )
    }


  // =======================================================
  // LOAD
  // =======================================================

  const loadDebts =
    async () => {
      try {
        setLoading(true)

        const data =
          await getDebts()

        setDebts(
          Array.isArray(data)
            ? data
            : []
        )
      } catch (error) {
        console.error(
          "Error loading debts:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les dettes."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  const loadFormData =
    async () => {
      try {
        const data =
          await getDebtFormData()

        setCustomers(
          Array.isArray(
            data?.customers
          )
            ? data.customers
            : []
        )

        setStores(
          Array.isArray(
            data?.stores
          )
            ? data.stores
            : []
        )

        setUsers(
          Array.isArray(
            data?.users
          )
            ? data.users
            : []
        )

        setDebtTypes(
          Array.isArray(
            data?.debt_types
          )
            ? data.debt_types
            : []
        )

        setStatuses(
          Array.isArray(
            data?.statuses
          )
            ? data.statuses
            : []
        )

        setReferenceTypes(
          Array.isArray(
            data?.reference_types
          )
            ? data.reference_types
            : []
        )
      } catch (error) {
        console.error(
          "Error loading debt form data:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les données du formulaire."
          ),
          "error"
        )
      }
    }


  useEffect(() => {
    loadDebts()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // FORM
  // =======================================================

  const resetForm =
    () => {
      setSelectedDebt(
        null
      )

      setFormData({
        ...defaultFormData,
      })
    }


  const openAddDialog =
    () => {
      resetForm()
      setOpen(true)
    }


  const openEditDialog =
    async (
      debtId
    ) => {
      try {
        const debt =
          await getDebtById(
            debtId
          )

        setSelectedDebt(
          debt
        )

        setFormData({
          company_id:
            debt.company_id ??
            1,

          debt_type:
            debt.debt_type ||
            "",

          customer_id:
            debt.customer_id ??
            "",

          store_id:
            debt.store_id ??
            "",

          user_id:
            debt.user_id ??
            "",

          reference_type:
            debt.reference_type ||
            "",

          reference_id:
            debt.reference_id !==
              null &&
            debt.reference_id !==
              undefined
              ? Number(
                  debt.reference_id
                )
              : "",

          external_reference:
            debt.external_reference ||
            "",

          original_amount:
            debt.original_amount !==
              null &&
            debt.original_amount !==
              undefined
              ? Number(
                  debt.original_amount
                )
              : "",

          paid_amount:
            debt.paid_amount !==
              null &&
            debt.paid_amount !==
              undefined
              ? Number(
                  debt.paid_amount
                )
              : 0,

          status:
            debt.status ||
            "open",

          due_date:
            formatDateTimeForInput(
              debt.due_date
            ),

          description:
            debt.description ||
            "",

          notes:
            debt.notes ||
            "",

          created_by:
            debt.created_by ??
            1,
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger la dette."
          ),
          "error"
        )
      }
    }


  const handleClose =
    () => {
      if (
        saving
      ) {
        return
      }

      setOpen(false)
      resetForm()
    }


  const handleChange =
    (event) => {
      const {
        name,
        value,
      } =
        event.target

      setFormData(
        (previous) => ({
          ...previous,

          [name]:
            [
              "company_id",
              "customer_id",
              "store_id",
              "user_id",
              "reference_id",
              "created_by",
            ].includes(name)
              ? value === ""
                ? ""
                : Number(value)
              : [
                  "original_amount",
                  "paid_amount",
                ].includes(name)
              ? value === ""
                ? ""
                : Number(value)
              : value,
        })
      )
    }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm =
    () => {
      if (
        !formData.debt_type
      ) {
        showSnackbar(
          "Le type de dette est obligatoire.",
          "warning"
        )

        return false
      }


      if (
        formData.original_amount ===
          "" ||
        Number(
          formData.original_amount
        ) < 0
      ) {
        showSnackbar(
          "Le montant initial est obligatoire.",
          "warning"
        )

        return false
      }


      if (
        Number(
          formData.paid_amount ||
            0
        ) >
        Number(
          formData.original_amount ||
            0
        )
      ) {
        showSnackbar(
          "Le montant payé ne peut pas dépasser le montant initial.",
          "warning"
        )

        return false
      }

      return true
    }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      const payload = {
        company_id:
          Number(
            formData.company_id ||
              1
          ),

        debt_type:
          formData.debt_type,

        customer_id:
          formData.customer_id
            ? Number(
                formData.customer_id
              )
            : null,

        store_id:
          formData.store_id
            ? Number(
                formData.store_id
              )
            : null,

        user_id:
          formData.user_id
            ? Number(
                formData.user_id
              )
            : null,

        reference_type:
          formData.reference_type ||
          null,

        reference_id:
          formData.reference_id ===
          ""
            ? null
            : Number(
                formData.reference_id
              ),

        external_reference:
          formData.external_reference
            ?.trim() ||
          null,

        original_amount:
          Number(
            formData.original_amount ||
              0
          ),

        paid_amount:
          Number(
            formData.paid_amount ||
              0
          ),

        status:
          formData.status ||
          "open",

        due_date:
          formData.due_date ||
          null,

        description:
          formData.description
            ?.trim() ||
          null,

        notes:
          formData.notes
            ?.trim() ||
          null,

        created_by:
          Number(
            formData.created_by ||
              1
          ),
      }

      try {
        setSaving(true)

        if (
          selectedDebt?.id
        ) {
          await updateDebt(
            selectedDebt.id,
            payload
          )

          showSnackbar(
            "Dette modifiée avec succès."
          )
        } else {
          await createDebt(
            payload
          )

          showSnackbar(
            "Dette créée avec succès."
          )
        }

        /*
         * Fermeture directe :
         * handleClose() ne ferme pas pendant saving === true.
         */
        setOpen(false)
        resetForm()

        await loadDebts()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible d'enregistrer la dette."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete =
    async (
      debtId
    ) => {
      const confirmed =
        window.confirm(
          "Voulez-vous vraiment supprimer cette dette ?"
        )

      if (
        !confirmed
      ) {
        return
      }

      try {
        await deleteDebt(
          debtId
        )

        showSnackbar(
          "Dette supprimée avec succès."
        )

        await loadDebts()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de supprimer la dette."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // FILTER
  // =======================================================

  const filteredDebts =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return debts.filter(
        (item) => {
          const matchesSearch =
            keyword === "" ||
            [
              item.debt_code,
              item.debt_type,
              getDebtTypeLabel(
                item.debt_type
              ),
              item.customer_name,
              item.store_name,
              item.user_name,
              item.description,
              item.external_reference,
            ].some(
              (value) =>
                String(
                  value || ""
                )
                  .toLowerCase()
                  .includes(
                    keyword
                  )
            )

          const matchesStatus =
            statusFilter === "" ||
            String(
              item.status || ""
            ) ===
              statusFilter

          const matchesDebtType =
            debtTypeFilter === "" ||
            String(
              item.debt_type || ""
            ) ===
              debtTypeFilter

          return (
            matchesSearch &&
            matchesStatus &&
            matchesDebtType
          )
        }
      )
    }, [
      debts,
      search,
      statusFilter,
      debtTypeFilter,
      debtTypes,
    ])


  // =======================================================
  // SUMMARY
  // =======================================================

  const summary =
    useMemo(() => {
      return filteredDebts.reduce(
        (
          accumulator,
          item
        ) => {
          accumulator.count +=
            1

          accumulator.original +=
            toNumber(
              item.original_amount
            )

          accumulator.paid +=
            toNumber(
              item.paid_amount
            )

          accumulator.remaining +=
            toNumber(
              item.remaining_amount
            )

          return accumulator
        },
        {
          count: 0,
          original: 0,
          paid: 0,
          remaining: 0,
        }
      )
    }, [
      filteredDebts,
    ])


  // =======================================================
  // PAGINATION
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,

        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })
    )
  }, [
    search,
    statusFilter,
    debtTypeFilter,
  ])


  const paginatedMobileRows =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return filteredDebts.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredDebts,
      paginationModel.page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredDebts.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,

          pageSize:
            ROWS_PER_PAGE,
        })
      )
    }
  }, [
    filteredDebts.length,
    paginationModel.page,
  ])


  const resetFilters =
    () => {
      setSearch("")
      setDebtTypeFilter("")
      setStatusFilter("")

      setPaginationModel({
        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })
    }


  // =======================================================
  // COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "debt",

          headerName:
            "Dette",

          minWidth: 210,

          flex: 1.15,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.16}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: "0.86rem",
                    fontWeight: 900,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.debt_code ||
                    ""
                  }
                >
                  {params.row.debt_code ||
                    `DET-${params.row.id}`}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getDebtTypeLabel(
                    params.row.debt_type
                  )}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "concerned",

          headerName:
            "Concerné",

          minWidth: 205,

          flex: 1.15,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.14}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#334155",
                    fontSize: "0.83rem",
                    fontWeight: 800,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.row.customer_name ||
                    params.row.store_name ||
                    params.row.user_name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.15,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.row.customer_name &&
                  params.row.store_name
                    ? `Shop : ${params.row.store_name}`
                    : params.row.store_name &&
                      params.row.user_name
                    ? `Utilisateur : ${params.row.user_name}`
                    : params.row.external_reference ||
                      "Aucune référence externe"}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "amounts",

          headerName:
            "Montants",

          minWidth: 210,

          flex: 1.1,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.12}

                sx={{
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: "0.83rem",
                    fontWeight: 900,
                    lineHeight: 1.2,
                  }}
                >
                  Initial :{" "}
                  {formatAmount(
                    params.row.original_amount
                  )}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#15803d",
                    fontWeight: 800,
                    lineHeight: 1.15,
                  }}
                >
                  Payé :{" "}
                  {formatAmount(
                    params.row.paid_amount
                  )}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color:
                      toNumber(
                        params.row.remaining_amount
                      ) > 0
                        ? "#dc2626"
                        : "#15803d",

                    fontWeight: 900,
                    lineHeight: 1.15,
                  }}
                >
                  Reste :{" "}
                  {formatAmount(
                    params.row.remaining_amount
                  )}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "due_date",

          headerName:
            "Échéance",

          width: 120,

          renderCell:
            (params) => (
              <Typography
                sx={{
                  color: "#475569",
                  fontSize: "0.79rem",
                  fontWeight: 700,
                }}
              >
                {formatDate(
                  params.value
                )}
              </Typography>
            ),
        },


        {
          field:
            "status",

          headerName:
            "Statut",

          width: 145,

          renderCell:
            (params) => (
              <Chip
                size="small"
                color={getStatusColor(
                  params.value
                )}
                label={getStatusLabel(
                  params.value
                )}
                sx={{
                  maxWidth: 135,

                  "& .MuiChip-label":
                    {
                      overflow:
                        "hidden",

                      textOverflow:
                        "ellipsis",
                    },
                }}
              />
            ),
        },


        {
          field: "actions",

          headerName:
            "Actions",

          width: 185,

          sortable: false,

          filterable: false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => (
              <Stack
                direction="row"

                spacing={0.55}

                sx={{
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  disableElevation

                  onClick={() =>
                    openEditDialog(
                      params.row.id
                    )
                  }

                  sx={{
                    minWidth: 78,
                    px: 1,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Modifier
                </Button>


                <Button
                  variant="outlined"
                  color="error"
                  size="small"

                  onClick={() =>
                    handleDelete(
                      params.row.id
                    )
                  }

                  sx={{
                    minWidth: 82,
                    px: 0.9,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  Supprimer
                </Button>
              </Stack>
            ),
        },
      ],
      [
        debtTypes,
        statuses,
      ]
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              minHeight: 40,

              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 800,

              boxShadow: "none",
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,

              bgcolor: "#ffffff",
            },

          "& .MuiChip-root":
            {
              fontWeight: 800,
            },

          "& .MuiPaper-root":
            {
              backgroundImage:
                "none",
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "minmax(0, 1fr) auto",
            },

            gap: 1.5,

            alignItems: "start",

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Dettes
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"

              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 800,
              }}
            >
              Suivi des dettes clients, shops, distributeurs et
              autres engagements enregistrés.
            </Typography>
          </Box>


          <Button
            variant="contained"
            disableElevation

            onClick={
              openAddDialog
            }

            sx={{
              minWidth: {
                xs: "100%",
                sm: 170,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            + Nouvelle dette
          </Button>
        </Box>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              md:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 1.1,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Dettes"

            value={
              summary.count
            }

            caption="Lignes affichées"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <ReceiptLongRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Montant initial"

            value={
              formatAmount(
                summary.original
              )
            }

            caption="Valeur totale des dettes"

            accent="#7c3aed"

            background="rgba(124, 58, 237, 0.09)"

            icon={
              <AccountBalanceWalletRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Déjà payé"

            value={
              formatAmount(
                summary.paid
              )
            }

            caption="Montant déjà réglé"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <PaymentsRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Reste à payer"

            value={
              formatAmount(
                summary.remaining
              )
            }

            caption="Solde encore ouvert"

            accent="#dc2626"

            background="rgba(220, 38, 38, 0.09)"

            icon={
              <HourglassTopRoundedIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(0, 1fr) 220px 210px 135px",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"

              label="Recherche"

              placeholder="Code, client, shop, type, description..."

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
            />


            <TextField
              select
              size="small"
              fullWidth

              label="Type de dette"

              value={
                debtTypeFilter
              }

              onChange={(
                event
              ) =>
                setDebtTypeFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              {debtTypes.map(
                (type) => (
                  <MenuItem
                    key={
                      type.value
                    }

                    value={
                      type.value
                    }
                  >
                    {type.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select
              size="small"
              fullWidth

              label="Statut"

              value={
                statusFilter
              }

              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              {statuses.map(
                (status) => (
                  <MenuItem
                    key={
                      status.value
                    }

                    value={
                      status.value
                    }
                  >
                    {status.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              sx={{
                width: "100%",
                minWidth: 0,
                whiteSpace:
                  "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des dettes
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Montants initiaux, paiements, soldes et échéances
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredDebts.length} dette${
                filteredDebts.length >
                1
                  ? "s"
                  : ""
              }`}
            />
          </Box>


          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflowX: "auto",

                bgcolor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  minWidth: 1080,
                }}
              >
                <DataGrid
                  autoHeight

                  rows={
                    filteredDebts
                  }

                  columns={
                    columns
                  }

                  loading={
                    loading
                  }

                  disableRowSelectionOnClick

                  disableColumnSelector

                  pagination

                  paginationMode="client"

                  paginationModel={
                    paginationModel
                  }

                  onPaginationModelChange={(
                    model
                  ) =>
                    setPaginationModel({
                      page:
                        model.page,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  }

                  pageSizeOptions={[
                    ROWS_PER_PAGE,
                  ]}

                  rowHeight={72}

                  columnHeaderHeight={50}

                  getRowClassName={(
                    params
                  ) =>
                    getDebtRowClass(
                      params.row.status
                    )
                  }

                  localeText={{
                    noRowsLabel:
                      "Aucune dette",

                    noResultsOverlayLabel:
                      "Aucun résultat",

                    MuiTablePagination:
                      {
                        labelRowsPerPage:
                          "",

                        labelDisplayedRows:
                          ({
                            from,
                            to,
                            count,
                          }) =>
                            `${from}–${to} sur ${count}`,
                      },
                  }}

                  sx={{
                    border: 0,

                    width: "100%",

                    color: "#334155",

                    fontSize: "0.83rem",

                    bgcolor:
                      "#ffffff",

                    "& .MuiDataGrid-columnHeaders":
                      {
                        bgcolor:
                          "#f8fafc",

                        borderBottom:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiDataGrid-columnHeader":
                      {
                        bgcolor:
                          "#f8fafc",

                        px: 1.25,

                        borderRight:
                          "1px solid rgba(226, 232, 240, 0.60)",
                      },

                    "& .MuiDataGrid-columnHeaderTitle":
                      {
                        color:
                          "#334155",

                        fontSize:
                          "0.76rem",

                        fontWeight: 900,

                        letterSpacing:
                          "0.025em",
                      },

                    "& .MuiDataGrid-cell":
                      {
                        px: 1.25,

                        minWidth: 0,

                        display: "flex",

                        alignItems:
                          "center",

                        overflow:
                          "hidden",

                        borderBottom:
                          "1px solid #edf2f7",

                        borderRight:
                          "1px solid rgba(241, 245, 249, 0.85)",
                      },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .debt-row":
                      {
                        transition:
                          "background-color 0.16s ease, box-shadow 0.16s ease",
                      },

                    "& .debt-row--paid":
                      {
                        boxShadow:
                          "inset 4px 0 0 #22c55e",

                        bgcolor:
                          "rgba(240, 253, 244, 0.30)",
                      },

                    "& .debt-row--partial":
                      {
                        boxShadow:
                          "inset 4px 0 0 #f59e0b",

                        bgcolor:
                          "rgba(255, 251, 235, 0.34)",
                      },

                    "& .debt-row--open":
                      {
                        boxShadow:
                          "inset 4px 0 0 #0ea5e9",

                        bgcolor:
                          "rgba(240, 249, 255, 0.34)",
                      },

                    "& .debt-row--cancelled":
                      {
                        boxShadow:
                          "inset 4px 0 0 #ef4444",

                        bgcolor:
                          "rgba(254, 242, 242, 0.30)",
                      },

                    "& .debt-row--default":
                      {
                        boxShadow:
                          "inset 4px 0 0 #cbd5e1",
                      },

                    "& .debt-row:hover":
                      {
                        bgcolor:
                          "#f8fbff !important",

                        boxShadow:
                          "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                      },

                    "& .MuiDataGrid-footerContainer":
                      {
                        minHeight: 52,

                        bgcolor:
                          "#fbfdff",

                        borderTop:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiTablePagination-toolbar":
                      {
                        minHeight: 52,
                        px: 1.25,
                      },

                    "& .MuiTablePagination-selectLabel":
                      {
                        display: "none",
                      },

                    "& .MuiTablePagination-input":
                      {
                        display: "none",
                      },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: {
                  xs: 1.1,
                  sm: 1.25,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des dettes...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucune dette trouvée.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileRows.map(
                    (row) => (
                      <DebtMobileCard
                        key={
                          row.id
                        }

                        row={
                          row
                        }

                        statusLabel={
                          getStatusLabel
                        }

                        debtTypeLabel={
                          getDebtTypeLabel
                        }

                        onEdit={
                          openEditDialog
                        }

                        onDelete={
                          handleDelete
                        }
                      />
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"

                count={
                  filteredDebts.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          handleClose
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            m: {
              xs: 0,
              sm: 2,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "92dvh",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.3,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedDebt
              ? "Modifier la dette"
              : "Nouvelle dette"}
          </Typography>


          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            Enregistrez le type, la personne ou le shop concerné,
            les montants et l’échéance.
          </Typography>
        </DialogTitle>


        <DialogContent dividers>
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >
            <Paper
              variant="outlined"

              sx={{
                p: 1.5,
                borderRadius: 3,
                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Identification
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",
                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"

                  label="Type de dette"

                  name="debt_type"

                  value={
                    formData.debt_type
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }
                >
                  {debtTypes.map(
                    (type) => (
                      <MenuItem
                        key={
                          type.value
                        }

                        value={
                          type.value
                        }
                      >
                        {type.label}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  select
                  size="small"

                  label="Statut"

                  name="status"

                  value={
                    formData.status
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  {statuses.map(
                    (status) => (
                      <MenuItem
                        key={
                          status.value
                        }

                        value={
                          status.value
                        }
                      >
                        {status.label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Box>
            </Paper>


            <Paper
              variant="outlined"

              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#f8fafc",
                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Concerné
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",
                    md:
                      "repeat(3, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"

                  label="Client"

                  name="customer_id"

                  value={
                    formData.customer_id
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="">
                    Aucun
                  </MenuItem>

                  {customers.map(
                    (customer) => (
                      <MenuItem
                        key={
                          customer.id
                        }

                        value={
                          customer.id
                        }
                      >
                        {customer.code} -{" "}
                        {customer.name}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  select
                  size="small"

                  label="Shop"

                  name="store_id"

                  value={
                    formData.store_id
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="">
                    Aucun
                  </MenuItem>

                  {stores.map(
                    (store) => (
                      <MenuItem
                        key={
                          store.id
                        }

                        value={
                          store.id
                        }
                      >
                        {store.code} -{" "}
                        {store.name}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  select
                  size="small"

                  label="Utilisateur"

                  name="user_id"

                  value={
                    formData.user_id
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="">
                    Aucun
                  </MenuItem>

                  {users.map(
                    (user) => (
                      <MenuItem
                        key={
                          user.id
                        }

                        value={
                          user.id
                        }
                      >
                        {user.label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Box>
            </Paper>


            <Paper
              variant="outlined"

              sx={{
                p: 1.5,
                borderRadius: 3,
                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Référence
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",
                    md:
                      "repeat(3, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"

                  label="Type référence"

                  name="reference_type"

                  value={
                    formData.reference_type
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="">
                    Aucune
                  </MenuItem>

                  {referenceTypes.map(
                    (type) => (
                      <MenuItem
                        key={
                          type.value
                        }

                        value={
                          type.value
                        }
                      >
                        {type.label}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  size="small"

                  label="ID référence"

                  name="reference_id"

                  type="number"

                  value={
                    formData.reference_id
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />


                <TextField
                  size="small"

                  label="Référence externe"

                  name="external_reference"

                  value={
                    formData.external_reference
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                />
              </Box>
            </Paper>


            <Paper
              variant="outlined"

              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#f8fafc",
                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Montants et échéance
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",
                    md:
                      "repeat(3, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"

                  label="Montant initial"

                  name="original_amount"

                  type="number"

                  value={
                    formData.original_amount
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}
                />


                <TextField
                  size="small"

                  label="Montant déjà payé"

                  name="paid_amount"

                  type="number"

                  value={
                    formData.paid_amount
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}
                />


                <TextField
                  type="datetime-local"
                  size="small"

                  label="Échéance"

                  name="due_date"

                  value={
                    formData.due_date
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            </Paper>


            <TextField
              label="Description"

              name="description"

              value={
                formData.description
              }

              onChange={
                handleChange
              }

              fullWidth

              multiline

              minRows={2}

              disabled={
                saving
              }
            />


            <TextField
              label="Notes"

              name="notes"

              value={
                formData.notes
              }

              onChange={
                handleChange
              }

              fullWidth

              multiline

              minRows={2}

              disabled={
                saving
              }
            />
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"
            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedDebt
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>


      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          handleCloseSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }

          severity={
            snackbar.severity
          }

          variant="filled"

          sx={{
            width: "100%",
            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default DebtsPage
